import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export const exportOpenTransactionsToExcel = async (data, reportName, translations) => {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(translations.sheetName);

    // Set RTL for Arabic
    worksheet.views = [{ rightToLeft: true }];

    // Title
    const titleRow = worksheet.addRow([translations.reportTitle]);
    titleRow.font = { size: 20, bold: true, color: { argb: 'FFFFFF' } };
    titleRow.alignment = { horizontal: 'center', vertical: 'middle' };
    titleRow.height = 35;
    worksheet.mergeCells('A1:F1');
    worksheet.getCell('A1').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '4472C4' }
    };
    worksheet.getCell('A1').border = {
      top: { style: 'thick' },
      left: { style: 'thick' },
      bottom: { style: 'thick' },
      right: { style: 'thick' }
    };

    worksheet.addRow([]);

    // User info section with styling
    const userInfoData = [
      [translations.userName, data.userName],
      [translations.barcode, data.barcode],
      [translations.patronGroup, data.patronGroup],
      [translations.reportDate, new Date().toLocaleDateString('ar-SA')]
    ];

    userInfoData.forEach(([label, value]) => {
      const row = worksheet.addRow([label, value]);
      row.font = { size: 12 };
      row.getCell(1).font = { bold: true };
      row.getCell(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E7E6E6' }
      };
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { vertical: 'middle' };
      });
    });

    worksheet.addRow([]);

    // Summary section with enhanced styling
    const summaryHeaderRow = worksheet.addRow([translations.summary]);
    summaryHeaderRow.font = { size: 14, bold: true, color: { argb: 'FFFFFF' } };
    summaryHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' };
    summaryHeaderRow.height = 25;
    worksheet.mergeCells(`A${summaryHeaderRow.number}:B${summaryHeaderRow.number}`);
    worksheet.getCell(`A${summaryHeaderRow.number}`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '70AD47' }
    };
    worksheet.getCell(`A${summaryHeaderRow.number}`).border = {
      top: { style: 'medium' },
      left: { style: 'medium' },
      bottom: { style: 'medium' },
      right: { style: 'medium' }
    };

    const summaryData = [
      [translations.totalOpenLoans, data.openLoansCount],
      [translations.totalOpenRequests, data.openRequestsCount],
      [translations.totalOutstandingFees, data.outstandingFeesCount],
      [translations.totalFeeAmount, `${data.totalFeeAmount} ${translations.currency}`]
    ];

    summaryData.forEach(([label, value]) => {
      const row = worksheet.addRow([label, value]);
      row.font = { size: 11 };
      row.getCell(1).font = { bold: true };
      row.getCell(2).alignment = { horizontal: 'center' };
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { vertical: 'middle' };
      });
    });

    worksheet.addRow([]);

    // Open Loans section
    if (data.openLoans && data.openLoans.length > 0) {
      const loansSectionRow = worksheet.addRow([translations.openLoans]);
      loansSectionRow.font = { size: 14, bold: true, color: { argb: 'FFFFFF' } };
      loansSectionRow.alignment = { horizontal: 'center', vertical: 'middle' };
      loansSectionRow.height = 25;
      worksheet.mergeCells(`A${loansSectionRow.number}:F${loansSectionRow.number}`);
      worksheet.getCell(`A${loansSectionRow.number}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'ED7D31' }
      };
      worksheet.getCell(`A${loansSectionRow.number}`).border = {
        top: { style: 'medium' },
        left: { style: 'medium' },
        bottom: { style: 'medium' },
        right: { style: 'medium' }
      };

      const loansHeaderRow = worksheet.addRow([
        translations.itemTitle,
        translations.itemBarcode,
        translations.loanDate,
        translations.dueDate,
        translations.overdueDays,
        translations.status,
      ]);
      loansHeaderRow.font = { bold: true, size: 11 };
      loansHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' };
      loansHeaderRow.height = 20;
      loansHeaderRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF2CC' }
      };
      loansHeaderRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'medium' },
          right: { style: 'thin' }
        };
      });

      data.openLoans.forEach(loan => {
        const row = worksheet.addRow([
          loan.itemTitle,
          loan.itemBarcode,
          loan.loanDate,
          loan.dueDate,
          loan.overdueDays || 0,
          loan.status,
        ]);
        row.alignment = { vertical: 'middle' };
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          if (colNumber === 5) {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          }
        });
      });
      worksheet.addRow([]);
    }

    // Open Requests section
    if (data.openRequests && data.openRequests.length > 0) {
      const requestsSectionRow = worksheet.addRow([translations.openRequests]);
      requestsSectionRow.font = { size: 14, bold: true, color: { argb: 'FFFFFF' } };
      requestsSectionRow.alignment = { horizontal: 'center', vertical: 'middle' };
      requestsSectionRow.height = 25;
      worksheet.mergeCells(`A${requestsSectionRow.number}:E${requestsSectionRow.number}`);
      worksheet.getCell(`A${requestsSectionRow.number}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '5B9BD5' }
      };
      worksheet.getCell(`A${requestsSectionRow.number}`).border = {
        top: { style: 'medium' },
        left: { style: 'medium' },
        bottom: { style: 'medium' },
        right: { style: 'medium' }
      };

      const requestsHeaderRow = worksheet.addRow([
        translations.itemTitle,
        translations.requestType,
        translations.requestDate,
        translations.requestStatus,
        translations.servicePoint,
      ]);
      requestsHeaderRow.font = { bold: true, size: 11 };
      requestsHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' };
      requestsHeaderRow.height = 20;
      requestsHeaderRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'DDEBF7' }
      };
      requestsHeaderRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'medium' },
          right: { style: 'thin' }
        };
      });

      data.openRequests.forEach(request => {
        const row = worksheet.addRow([
          request.itemTitle,
          request.requestType,
          request.requestDate,
          request.status,
          request.servicePoint,
        ]);
        row.alignment = { vertical: 'middle' };
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      });
      worksheet.addRow([]);
    }

    // Outstanding Fees section
    if (data.outstandingFees && data.outstandingFees.length > 0) {
      const feesSectionRow = worksheet.addRow([translations.outstandingFees]);
      feesSectionRow.font = { size: 14, bold: true, color: { argb: 'FFFFFF' } };
      feesSectionRow.alignment = { horizontal: 'center', vertical: 'middle' };
      feesSectionRow.height = 25;
      worksheet.mergeCells(`A${feesSectionRow.number}:E${feesSectionRow.number}`);
      worksheet.getCell(`A${feesSectionRow.number}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'C00000' }
      };
      worksheet.getCell(`A${feesSectionRow.number}`).border = {
        top: { style: 'medium' },
        left: { style: 'medium' },
        bottom: { style: 'medium' },
        right: { style: 'medium' }
      };

      const feesHeaderRow = worksheet.addRow([
        translations.feeType,
        translations.amount,
        translations.createdDate,
        translations.feeStatus,
        translations.relatedItem,
      ]);
      feesHeaderRow.font = { bold: true, size: 11 };
      feesHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' };
      feesHeaderRow.height = 20;
      feesHeaderRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FCE4D6' }
      };
      feesHeaderRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'medium' },
          right: { style: 'thin' }
        };
      });

      data.outstandingFees.forEach(fee => {
        const row = worksheet.addRow([
          fee.feeType,
          `${fee.amount} ${translations.currency}`,
          fee.createdDate,
          fee.status,
          fee.relatedItem || '-',
        ]);
        row.alignment = { vertical: 'middle' };
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          if (colNumber === 2) {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.font = { bold: true };
          }
        });
      });
      worksheet.addRow([]);
    }

    // Footer note
    worksheet.addRow([]);
    const noteRow = worksheet.addRow([translations.footerNote]);
    noteRow.font = { italic: true, bold: true, color: { argb: 'FFFFFF' }, size: 11 };
    noteRow.alignment = { horizontal: 'center', vertical: 'middle' };
    noteRow.height = 30;
    worksheet.mergeCells(`A${noteRow.number}:F${noteRow.number}`);
    worksheet.getCell(`A${noteRow.number}`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFC000' }
    };
    worksheet.getCell(`A${noteRow.number}`).border = {
      top: { style: 'medium' },
      left: { style: 'medium' },
      bottom: { style: 'medium' },
      right: { style: 'medium' }
    };

    // Set column widths
    worksheet.columns = [
      { width: 35 },
      { width: 20 },
      { width: 18 },
      { width: 18 },
      { width: 15 },
      { width: 20 }
    ];

    // Generate file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${reportName}.xlsx`);

    return { success: true };
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return { success: false, error };
  }
};
